import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

interface Lead {
  id: number
  first_name: string | null
  last_name: string | null
  email: string | null
  phone: string | null
  description: string | null
  status_id: number
  status_name: string
  status_color: string
  urgency_id: number
  urgency_name: string
  urgency_color: string
  priority: 'low' | 'medium' | 'high' | null
  best_time_to_contact: 'day' | 'evening' | 'weekend' | null
  task_count: number
  created_at: string
  creator_first_name: string
  creator_last_name: string
  assigned_to?: number
  assigned_to_name?: string
  assigned_at?: string
  partner_id?: number | null
  partner_name?: string | null
  partner_email?: string | null
}

interface LeadDetailModalProps {
  lead: Lead
  onClose: () => void
  onEdit: (lead: Lead) => void
  onConvert: (lead: Lead) => void
  onFollowUp: (lead: Lead) => void
  onDelete: (leadId: number) => void
}

// Helper function to calculate days since lead creation
const getDaysSinceCreation = (createdAt: string) => {
  const createdDate = new Date(createdAt)
  const currentDate = new Date()
  const timeDifference = currentDate.getTime() - createdDate.getTime()
  const daysDifference = Math.floor(timeDifference / (1000 * 3600 * 24))
  return daysDifference
}

// Helper function to format phone number for display
const formatPhoneDisplay = (phone: string): string => {
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '')
  
  // Format as (XXX) XXX-XXXX for 10 digits
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  
  // Format as +X (XXX) XXX-XXXX for 11 digits
  if (digits.length === 11) {
    return `+${digits.slice(0, 1)} (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
  }
  
  // Return as-is if not standard format
  return phone
}

// Helper function to get raw phone digits for copying
const getRawPhoneDigits = (phone: string): string => {
  return phone.replace(/\D/g, '')
}

// Helper function to make links clickable in text
const renderTextWithLinks = (text: string) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g
  const parts = text.split(urlRegex)
  
  return parts.map((part, index) => {
    if (urlRegex.test(part)) {
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-gray-900 dark:text-gray-600 hover:text-blue-800 dark:hover:text-blue-300 underline break-all"
        >
          {part}
        </a>
      )
    }
    return part
  })
}

const LeadDetailModal: React.FC<LeadDetailModalProps> = ({
  lead,
  onClose,
  onEdit,
  onConvert,
  onFollowUp,
  onDelete
}) => {
  const { t } = useTranslation()
  const [copiedField, setCopiedField] = useState<string | null>(null)

  const handleCopy = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedField(fieldName)
      setTimeout(() => setCopiedField(null), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  // Translation functions for status and urgency values
  const translateStatusName = (statusName: string): string => {
    const statusMap: { [key: string]: string } = {
      'New': t('leads.statusNew'),
      'Called': t('leads.statusCalled'),
      'Emailed': t('leads.statusEmailed'),
      'Waiting': t('leads.statusWaiting'),
      'Qualified': t('leads.statusQualified'),
      'Not Interested': t('leads.statusNotInterested'),
      'new': t('leads.statusNew'),
      'called': t('leads.statusCalled'),
      'emailed': t('leads.statusEmailed'),
      'waiting': t('leads.statusWaiting'),
      'qualified': t('leads.statusQualified'),
      'not interested': t('leads.statusNotInterested'),
    }
    return statusMap[statusName] || statusName
  }

  const translateUrgencyName = (urgencyName: string): string => {
    const urgencyMap: { [key: string]: string } = {
      'Low': t('leads.urgencyLow'),
      'Medium': t('leads.urgencyMedium'),
      'High': t('leads.urgencyHigh'),
      'low': t('leads.urgencyLow'),
      'medium': t('leads.urgencyMedium'),
      'high': t('leads.urgencyHigh'),
    }
    return urgencyMap[urgencyName] || urgencyName
  }

  const getDisplayName = (lead: Lead) => {
    if (lead.first_name && lead.last_name) {
      return `${lead.first_name} ${lead.last_name}`
    }
    if (lead.first_name) return lead.first_name
    if (lead.last_name) return lead.last_name
    if (lead.email) return lead.email
    return 'Unknown Lead'
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white dark:bg-gray-800 rounded-3xl w-full max-w-2xl shadow-2xl max-h-[95vh] overflow-hidden border border-gray-200 dark:border-gray-700 flex flex-col">
        {/* Header - Gradient */}
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 p-6 text-white flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mr-4">
                <span className="text-2xl font-bold text-white">
                  {getDisplayName(lead).charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <h2 className="text-2xl font-bold mb-2">
                  {getDisplayName(lead)}
                </h2>
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold"
                    style={{ backgroundColor: lead.status_color }}
                  >
                    {translateStatusName(lead.status_name)}
                  </span>
                  <span
                    className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold"
                    style={{ backgroundColor: lead.urgency_color }}
                  >
                    {translateUrgencyName(lead.urgency_name)}
                  </span>
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-white/20">
                    📅 {getDaysSinceCreation(lead.created_at)} {getDaysSinceCreation(lead.created_at) === 1 ? t('leads.day') : t('leads.days')}
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-full transition-colors flex-shrink-0"
              aria-label="Close modal"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-8">
          {/* Contact Information */}
          <div className="mb-8">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center">
              <svg className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              {t('leads.contactInformation')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {lead.email && (
                <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center flex-1 min-w-0">
                    <svg className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                    </svg>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">{t('leads.email')}</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white break-all">
                        {lead.email}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleCopy(lead.email!, 'email')}
                    className="ml-3 p-2 hover:bg-blue-100 dark:hover:bg-blue-800 rounded-lg transition-colors flex-shrink-0"
                    title={copiedField === 'email' ? t('common.copied') : t('common.copy')}
                  >
                    {copiedField === 'email' ? (
                      <svg className="h-4 w-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="h-4 w-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    )}
                  </button>
                </div>
              )}
              {lead.phone && (
                <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
                  <div className="flex items-center flex-1 min-w-0">
                    <svg className="h-5 w-5 text-green-600 dark:text-green-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">{t('leads.phone')}</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {formatPhoneDisplay(lead.phone)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleCopy(getRawPhoneDigits(lead.phone!), 'phone')}
                    className="ml-3 p-2 hover:bg-green-100 dark:hover:bg-green-800 rounded-lg transition-colors flex-shrink-0"
                    title={copiedField === 'phone' ? t('common.copied') : t('common.copy')}
                  >
                    {copiedField === 'phone' ? (
                      <svg className="h-4 w-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="h-4 w-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Lead Details */}
          {(lead.best_time_to_contact || lead.task_count > 0 || lead.assigned_to || lead.partner_id) && (
            <div className="mb-8">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                <svg className="h-5 w-5 text-purple-600 dark:text-purple-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {t('leads.leadDetails')}
              </h3>
              <div className="grid grid-cols-1 gap-3">
                {lead.partner_id && lead.partner_name && (
                  <div className="flex items-center p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl border border-indigo-200 dark:border-indigo-800">
                    <svg className="h-5 w-5 text-indigo-600 dark:text-indigo-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <div>
                      <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">{t('leads.source')}</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {lead.partner_name}
                      </p>
                      {lead.partner_email && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {lead.partner_email}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {lead.best_time_to_contact && (
                  <div className="flex items-center p-4 bg-orange-50 dark:bg-orange-900/20 rounded-xl border border-orange-200 dark:border-orange-800">
                    <svg className="h-5 w-5 text-orange-600 dark:text-orange-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">{t('leads.bestContactTime')}</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {lead.best_time_to_contact.charAt(0).toUpperCase() + lead.best_time_to_contact.slice(1)}
                      </p>
                    </div>
                  </div>
                )}
                
                {lead.task_count > 0 && (
                  <div className="flex items-center p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl border border-yellow-200 dark:border-yellow-800">
                    <svg className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <div>
                      <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">{t('leads.tasks')}</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {lead.task_count} {lead.task_count > 1 ? t('leads.tasks') : t('leads.task')}
                      </p>
                    </div>
                  </div>
                )}

                {lead.assigned_to && (
                  <div className="flex items-center p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl border border-indigo-200 dark:border-indigo-800">
                    <svg className="h-5 w-5 text-indigo-600 dark:text-indigo-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <div>
                      <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">{t('leads.assignedTo')}</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {lead.assigned_to_name}
                      </p>
                      {lead.assigned_at && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {t('leads.assignedOn')} {new Date(lead.assigned_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Description */}
          {lead.description && (
            <div className="mb-8">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                <svg className="h-5 w-5 text-gray-600 dark:text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                </svg>
                {t('leads.description')}
              </h3>
              <div className="p-5 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600">
                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                  {renderTextWithLinks(lead.description)}
                </p>
              </div>
            </div>
          )}

          {/* Timeline */}
          <div className="mb-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center">
              <svg className="h-5 w-5 text-gray-600 dark:text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {t('leads.timeline')}
            </h3>
            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600">
              <div className="flex items-center">
                <svg className="h-5 w-5 text-gray-600 dark:text-gray-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <div>
                  <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">{t('leads.created')}</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {new Date(lead.created_at).toLocaleDateString()} {t('leads.by')} {lead.creator_first_name} {lead.creator_last_name}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Actions Footer */}
        <div className="bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-600 px-8 py-5 flex-shrink-0">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <button
              onClick={onClose}
              className="px-5 py-2.5 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl font-medium transition-colors"
            >
              {t('leads.close')}
            </button>
            <div className="flex items-center space-x-3 flex-wrap">
              <button
                onClick={() => onFollowUp(lead)}
                className="px-5 py-2.5 bg-gray-800 hover:bg-gray-700 text-white text-sm font-semibold rounded-xl transition-all duration-200 flex items-center shadow-md hover:shadow-lg"
              >
                <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {t('leads.followUp')}
              </button>
              <button
                onClick={() => onConvert(lead)}
                className="px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-xl transition-all duration-200 shadow-md hover:shadow-lg"
              >
                {t('leads.convert')}
              </button>
              <button
                onClick={() => onEdit(lead)}
                className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl transition-all duration-200 shadow-md hover:shadow-lg"
              >
                {t('leads.edit')}
              </button>
              <button
                onClick={() => onDelete(lead.id)}
                className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-xl transition-all duration-200 shadow-md hover:shadow-lg"
              >
                {t('leads.delete')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LeadDetailModal
